import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isSessionExpired, retryUnlessSessionExpired } from "@/api/polling";
import { listingAgentResearchApi, type ListingAgentResearchRunDto } from "@/api/listingAgentResearch";
import { listingAgentResearchKeys } from "@/hooks/buyers/listingAgentResearchKeys";

/** How often a live run is re-read (D12). */
export const AGENT_RESEARCH_POLL_MS = 5000;

export function useAgentResearchWorkspace(listingId: string) {
  return useQuery({
    queryKey: listingAgentResearchKeys.workspace(listingId),
    queryFn: () => listingAgentResearchApi.workspace(listingId),
    enabled: Boolean(listingId),
  });
}

/** The card line's read (D18) — counts only, cheap enough for every Overview. */
export function useAgentResearchSummary(listingId: string) {
  return useQuery({
    queryKey: listingAgentResearchKeys.summary(listingId),
    queryFn: () => listingAgentResearchApi.summary(listingId),
    enabled: Boolean(listingId),
  });
}

/**
 * One run, re-read every five seconds while it is live (D12).
 *
 * <p>A run finishes on the SERVER, so nothing on the client can hang an
 * invalidation off that moment except this poll. When a run this hook watched
 * in flight turns terminal, the workspace and the summary are invalidated ONCE
 * so the run list and the card line stop saying "in progress" — the buyer
 * Research tab's precedent (`useResearchRun`). A run already finished on first
 * sight invalidates nothing.</p>
 */
export function useAgentResearchRun(listingId: string, runId: string | null) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: listingAgentResearchKeys.run(listingId, runId ?? ""),
    queryFn: () => listingAgentResearchApi.run(listingId, runId!),
    enabled: Boolean(listingId && runId),
    refetchInterval: (q) => {
      if (isSessionExpired(q.state.error)) return false;
      const d: ListingAgentResearchRunDto | undefined = q.state.data;
      return d?.isActive ? AGENT_RESEARCH_POLL_MS : false;
    },
    retry: retryUnlessSessionExpired,
  });

  const watchedLive = useRef<string | null>(null);
  const data = query.data;
  useEffect(() => {
    if (!data) return;
    if (data.isActive) {
      watchedLive.current = data.id;
      return;
    }
    if (watchedLive.current === data.id) {
      watchedLive.current = null;
      void qc.invalidateQueries({ queryKey: listingAgentResearchKeys.workspace(listingId) });
      void qc.invalidateQueries({ queryKey: listingAgentResearchKeys.summary(listingId) });
    }
  }, [data, listingId, qc]);

  return query;
}
